import { useState } from 'react'
import Card from '@/components/ui/Card'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/utils/cn'
import { useTranslation } from 'react-i18next'

interface NavigationPanelProps {
  currentPage?: number
  currentSurah?: number
  currentJuz?: number
  viewMode: 'mushaf' | 'ayah'
  onPageChange?: (page: number) => void
  onSurahChange?: (surah: number) => void
  onJuzChange?: (juz: number) => void
}

export default function NavigationPanel({
  currentPage = 1,
  currentSurah = 1,
  currentJuz = 1,
  viewMode,
  onPageChange,
  onSurahChange,
  onJuzChange,
}: NavigationPanelProps) {
  const { t } = useTranslation()
  const [navType, setNavType] = useState<'page' | 'surah' | 'juz'>(
    viewMode === 'mushaf' ? 'page' : 'surah'
  )
  const [inputValue, setInputValue] = useState('')

  const handleNavigate = () => {
    const value = parseInt(inputValue, 10)
    if (isNaN(value)) return

    if (navType === 'page' && onPageChange && value >= 1 && value <= 604) {
      onPageChange(value)
    } else if (navType === 'surah' && onSurahChange && value >= 1 && value <= 114) {
      onSurahChange(value)
    } else if (navType === 'juz' && onJuzChange && value >= 1 && value <= 30) {
      onJuzChange(value)
    }
    setInputValue('')
  }

  const handlePrevious = () => {
    if (navType === 'page' && onPageChange && currentPage > 1) {
      onPageChange(currentPage - 1)
    } else if (navType === 'surah' && onSurahChange && currentSurah > 1) {
      onSurahChange(currentSurah - 1)
    } else if (navType === 'juz' && onJuzChange && currentJuz > 1) {
      onJuzChange(currentJuz - 1)
    }
  }

  const handleNext = () => {
    if (navType === 'page' && onPageChange && currentPage < 604) {
      onPageChange(currentPage + 1)
    } else if (navType === 'surah' && onSurahChange && currentSurah < 114) {
      onSurahChange(currentSurah + 1)
    } else if (navType === 'juz' && onJuzChange && currentJuz < 30) {
      onJuzChange(currentJuz + 1)
    }
  }

  const getCurrentValue = () => {
    if (navType === 'page') return currentPage
    if (navType === 'surah') return currentSurah
    return currentJuz
  }

  const getMaxValue = () => {
    if (navType === 'page') return 604
    if (navType === 'surah') return 114
    return 30
  }

  const canGoPrevious = getCurrentValue() > 1
  const canGoNext = getCurrentValue() < getMaxValue()

  return (
    <Card className="p-4">
      <div className="space-y-4">
        {/* Navigation Type Selector */}
        <div className="flex gap-2">
          <button
            onClick={() => setNavType('page')}
            className={cn(
              'flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
              navType === 'page'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            )}
          >
            {t('quran.nav.page', 'صفحة')}
          </button>
          <button
            onClick={() => setNavType('surah')}
            className={cn(
              'flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
              navType === 'surah'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            )}
          >
            {t('quran.nav.surah', 'سورة')}
          </button>
          <button
            onClick={() => setNavType('juz')}
            className={cn(
              'flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
              navType === 'juz'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            )}
          >
            {t('quran.nav.juz', 'جزء')}
          </button>
        </div>

        {/* Current Location Display */}
        <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
            {navType === 'page' && t('quran.nav.currentPage', 'الصفحة الحالية')}
            {navType === 'surah' && t('quran.nav.currentSurah', 'السورة الحالية')}
            {navType === 'juz' && t('quran.nav.currentJuz', 'الجزء الحالي')}
          </div>
          <div className="text-3xl font-bold text-primary-600 dark:text-primary-400">
            {getCurrentValue()}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
            {t('common.of', 'من')} {getMaxValue()}
          </div>
        </div>

        {/* Navigation Controls */}
        <div className="flex gap-2">
          <button
            onClick={handlePrevious}
            disabled={!canGoPrevious}
            className={cn(
              'p-2 rounded-lg transition-colors',
              canGoPrevious
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                : 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
            )}
            aria-label={t('common.previous', 'السابق')}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <button
            onClick={handleNext}
            disabled={!canGoNext}
            className={cn(
              'p-2 rounded-lg transition-colors',
              canGoNext
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                : 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
            )}
            aria-label={t('common.next', 'التالي')}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        </div>

        {/* Go to Specific Number */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('quran.nav.goTo', 'الانتقال إلى')} {navType === 'page' ? t('quran.nav.page', 'صفحة') : navType === 'surah' ? t('quran.nav.surah', 'سورة') : t('quran.nav.juz', 'جزء')}:
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              min="1"
              max={getMaxValue()}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleNavigate()}
              placeholder={`1-${getMaxValue()}`}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-200 text-center"
            />
            <button
              onClick={handleNavigate}
              disabled={!inputValue}
              className={cn(
                'px-4 py-2 rounded-lg font-medium transition-colors',
                inputValue
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
              )}
            >
              {t('quran.nav.go', 'انتقال')}
            </button>
          </div>
        </div>

        {/* Quick Info */}
        {viewMode === 'mushaf' && (
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <div className="flex justify-between">
              <span>{t('quran.nav.page', 'صفحة')}:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{currentPage}</span>
            </div>
            <div className="flex justify-between">
              <span>{t('quran.nav.juz', 'جزء')}:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{currentJuz}</span>
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}

